import type { SVGProps } from 'react';

/**
 * Line icons, ported from the mock's `icons.jsx`.
 *
 * Same 24×24 stroke geometry as the approved design, so the lock and the arrow on
 * the sign-in button are the shapes that were signed off rather than lookalikes.
 */
function Stroke(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
      // A default size, because an SVG with a viewBox and no dimensions resolves
      // to 100% of its container and swallows the page. Every call site used to
      // pass width and height, which hid that until one did not: a bare
      // <IconSearch /> rendered as a search icon the size of the table behind it.
      //
      // Before the spread, so a caller can still choose its own size.
      width={18}
      height={18}
      {...props}
    />
  );
}

export function IconLock(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Stroke>
  );
}

export function IconArrow(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </Stroke>
  );
}

export function IconPlus(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </Stroke>
  );
}

/**
 * The one shape here with no counterpart in the mock, which has no delete action
 * anywhere. Drawn in the same 24×24 stroke idiom. A trash can rather than the
 * mock's `IconX`, because an X reads as "dismiss this" — closing a dialog, or
 * clearing a filter — and these two buttons revoke somebody's access.
 */
export function IconTrash(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </Stroke>
  );
}

export function IconAlert(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Stroke>
  );
}

/* Sidebar navigation. Geometry copied from the mock's icons.jsx unchanged, so
   these are the same shapes that were signed off rather than lookalikes. */

export function IconDashboard(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </Stroke>
  );
}

export function IconContacts(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </Stroke>
  );
}

export function IconInteractions(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </Stroke>
  );
}

export function IconSettings(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </Stroke>
  );
}

export function IconLogout(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </Stroke>
  );
}

/* Contacts and the queue (S05, S06). Geometry from the mock's icons.jsx, unchanged. */

export function IconChevronLeft(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <polyline points="15 18 9 12 15 6" />
    </Stroke>
  );
}

export function IconChevronRight(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <polyline points="9 18 15 12 9 6" />
    </Stroke>
  );
}

export function IconSearch(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </Stroke>
  );
}

export function IconUpload(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </Stroke>
  );
}

/** The upload arrow, turned around — for the rejected-rows report. */
export function IconDownload(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </Stroke>
  );
}

export function IconPhone(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </Stroke>
  );
}

export function IconCalendar(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </Stroke>
  );
}

export function IconClock(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </Stroke>
  );
}

export function IconCheck(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <polyline points="20 6 9 17 4 12" />
    </Stroke>
  );
}

export function IconDoc(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </Stroke>
  );
}

export function IconX(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Stroke>
  );
}

/**
 * The dashboard and notification icons (S11, S12).
 *
 * Same 24×24 stroke geometry as the mock's, so the KPI cards carry the shapes
 * that were signed off. `IconTrend` is drawn as a rising line and rotated by CSS
 * for a fall, rather than there being a second path to keep in step with the
 * first.
 */
export function IconHeart(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
    </Stroke>
  );
}

export function IconDrop(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M12 2a7 7 0 0 1 7 7c0 3-2 5-3 7H8c-1-2-3-4-3-7a7 7 0 0 1 7-7z" />
    </Stroke>
  );
}

export function IconTrend(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </Stroke>
  );
}

export function IconBell(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </Stroke>
  );
}

/* The Avatar Assistant's chevron, for the product label in the header. Drawn
   at its stroke weight, 1.8, so the two headers match. */

export function IconChevronDown(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="6 9 12 15 18 9" />
    </Stroke>
  );
}

/* The theme switch's three choices, from the Avatar Assistant's icons. */

export function IconSun(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </Stroke>
  );
}

export function IconMoon(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />
    </Stroke>
  );
}

export function IconMonitor(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <Stroke {...props}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </Stroke>
  );
}
